import * as Sentry from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import {
  BrowserRouter,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import ErrorBoundary from './components/common/ErrorBoundary';
import CookieConsentBanner from './components/CookieConsentBanner';
import CookieConsentHandler from './components/CookieConsentHandler';
import {
  addSentryBreadcrumb,
  clearSentryUser,
  setSentryUser,
} from './config/sentry.config';
import { AuthProvider } from './contexts/AuthContext';
import { CookieConsentProvider } from './contexts/CookieContext';
import { useFirebaseAnalytics } from './hooks/useFirebaseAnalytics';
import { queryClient } from './QueryClient';
import { AppRoutes } from './routes/AppRoutes';

/**
 * Wrap Routes with Sentry for automatic routing instrumentation
 */
const SentryRoutes = Sentry.withSentryRouting(AppRoutes);

/**
 * Analytics and navigation tracking wrapper component
 * Tracks page views in Firebase Analytics and navigation breadcrumbs in Sentry
 * @returns {JSX.Element} Routes with analytics and Sentry tracking
 */
const RoutesWithAnalytics = () => {
  const location = useLocation();
  const navigationType = useNavigationType();

  // Automatically track page views in Firebase Analytics
  useFirebaseAnalytics();

  // Track navigation in Sentry as breadcrumbs
  useEffect(() => {
    try {
      addSentryBreadcrumb({
        category: 'navigation',
        message: `Navigated to ${location.pathname}`,
        level: 'info',
        data: {
          pathname: location.pathname,
          search: location.search,
          hash: location.hash,
          navigationType, // POP, PUSH, or REPLACE
        },
      });
    } catch (error) {
      console.error('Error tracking navigation in Sentry:', error);
    }
  }, [location, navigationType]);

  return <SentryRoutes />;
};

/**
 * Auth context wrapper with Sentry user tracking
 * Automatically sets/clears Sentry user context on auth changes
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} Auth provider with Sentry integration
 */
const AuthProviderWithSentry = ({ children }) => {
  /**
   * Handle user login - set Sentry user context
   * @param {Object} user - User object from authentication
   */
  const handleLogin = (user) => {
    if (!user) return;

    try {
      // Set Sentry user context when user logs in
      setSentryUser({
        id: user.uid || user.id,
        email: user.email,
        username: user.displayName || user.username || user.email,
        extra: {
          emailVerified: user.emailVerified,
          createdAt: user.metadata?.creationTime,
        },
      });

      // Add breadcrumb for login
      addSentryBreadcrumb({
        category: 'auth',
        message: 'User logged in',
        level: 'info',
        data: {
          userId: user.uid || user.id,
          email: user.email,
        },
      });
    } catch (error) {
      console.error('Error setting Sentry user context:', error);
    }
  };

  /**
   * Handle user logout - clear Sentry user context
   */
  const handleLogout = () => {
    try {
      // Clear Sentry user context when user logs out
      clearSentryUser();

      // Add breadcrumb for logout
      addSentryBreadcrumb({
        category: 'auth',
        message: 'User logged out',
        level: 'info',
      });
    } catch (error) {
      console.error('Error clearing Sentry user context:', error);
    }
  };

  return (
    <AuthProvider onLogin={handleLogin} onLogout={handleLogout}>
      {children}
    </AuthProvider>
  );
};

/**
 * Main App component with authentication provider, cookie consent, and error boundary
 * Sets up the core application structure with error tracking and monitoring
 * @returns {JSX.Element} App component
 */
function App() {
  /**
   * Handle application-level errors
   * @param {Error} error - The error that occurred
   * @param {Object} errorInfo - Additional error information from React
   * @param {string} errorInfo.componentStack - Component stack trace
   */
  const handleError = (error, errorInfo) => {
    try {
      // Log to console in development
      if (import.meta.env.MODE === 'development') {
        console.error('Application Error:', error);
        console.error('Error Info:', errorInfo);
      }

      // Capture in Sentry with context
      Sentry.captureException(error, {
        contexts: {
          react: {
            componentStack: errorInfo.componentStack,
          },
        },
        tags: {
          errorBoundary: 'app',
          environment: import.meta.env.MODE,
        },
        level: 'error',
      });

      // Add breadcrumb for error
      addSentryBreadcrumb({
        category: 'error',
        message: `Application error: ${error.message}`,
        level: 'error',
        data: {
          errorName: error.name,
          errorMessage: error.message,
          errorStack: error.stack,
        },
      });
    } catch (sentryError) {
      console.error('Error reporting to Sentry:', sentryError);
    }
  };

  /**
   * Track cookie consent in Sentry
   */
  useEffect(() => {
    /**
     * Handle cookie consent events
     * @param {CustomEvent} event - Cookie consent event
     * @param {Object} event.detail - Event details
     * @param {boolean} event.detail.consent - Consent status
     */
    const handleCookieConsent = (event) => {
      try {
        addSentryBreadcrumb({
          category: 'user.consent',
          message: `Cookie consent: ${event.detail.consent}`,
          level: 'info',
          data: event.detail,
        });
      } catch (error) {
        console.error('Error tracking cookie consent:', error);
      }
    };

    window.addEventListener('cookieConsent', handleCookieConsent);

    return () => {
      window.removeEventListener('cookieConsent', handleCookieConsent);
    };
  }, []);

  return (
    <BrowserRouter>
      <ErrorBoundary
        onError={handleError}
        showDetails={import.meta.env.MODE === 'development'}
        enableSentry={true}
      >
        <CookieConsentProvider>
          <QueryClientProvider client={queryClient}>
            <AuthProviderWithSentry>
              <RoutesWithAnalytics />
              <CookieConsentBanner />
              <CookieConsentHandler />
              {/* More Development tools can be added here */}
              {/* {import.meta.env.MODE === 'development' && (
                <TokenExpiryTester />
              )} */}
            </AuthProviderWithSentry>
          </QueryClientProvider>
        </CookieConsentProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
